import { uiText, useUiLanguage } from '../../../../utils/uiTranslation';
import React, { useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { ref, push, set, update } from 'firebase/database';
import { database } from '../../../../firebase';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../contexts/LanguageContext';
import nationalizationService, {
  addNationalizationLifecycleEventToUpdates,
  announceNationalizationWorkforceChange
} from '../../../../services/nationalizationService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";

const OffboardModal = ({ employeeId, companyUID, employeeName, onClose, onSuccess }) => {
  useUiLanguage();
  const { currentUser } = useAuth();
  const { t } = useTranslation(['myteam', 'common']);
  const { isRTL, language } = useLanguage();
  const [lastWorkingDay, setLastWorkingDay] = useState('');
  const [reason, setReason] = useState('');
  const [offboardType, setOffboardType] = useState('resignation');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const safeEmployeeName = employeeName || t('drawerModals.common.unknownEmployee', 'Unknown Employee');

  // Set default last working day to 2 weeks from today
  React.useEffect(() => {
    const twoWeeksFromNow = new Date();
    twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
    setLastWorkingDay(twoWeeksFromNow.toISOString().split('T')[0]);
  }, []);

  const handleSave = async () => {
    if (!lastWorkingDay) {
      alert(t('drawerModals.offboard.errors.lastDay', 'Please enter the last working day'));
      return;
    }

    setSaving(true);
    try {
      const basePath = `companies/${companyUID}/employees/${employeeId}`;

      // Commit the HR change and its delivery marker as one Firebase operation.
      // This closes the previous crash window between saving and calling the API.
      const workforceUpdates = {
        [`${basePath}/lifecycle/status`]: 'offboarding',
        [`${basePath}/lifecycle/terminationDate`]: lastWorkingDay,
        [`${basePath}/lifecycle/terminationType`]: offboardType,
        [`${basePath}/lifecycle/terminationReason`]: reason || '',
        [`${basePath}/lifecycle/terminationNotes`]: notes || '',
        [`${basePath}/lifecycle/terminatedBy`]: currentUser?.displayName || currentUser?.email || 'HR Team',
        [`${basePath}/lifecycle/terminatedByUID`]: currentUser?.uid,
        [`${basePath}/lifecycle/terminatedAt`]: Date.now(),
        [`${basePath}/status`]: 'offboarding'
      };
      const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
        companyUID,
        employeeId,
        source: 'employee_offboarding',
        changeKinds: ['lifecycle']
      });
      await update(ref(database), workforceUpdates);
      let lifecycleSync = null;
      try {
        lifecycleSync = await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
          eventId: lifecycleEvent.eventId,
          source: 'employee_offboarding',
          changeKinds: ['lifecycle']
        });
      } catch (syncError) {
        console.warn('Nationalization offboarding sync is pending:', syncError);
      }
      announceNationalizationWorkforceChange({
        companyUID,
        source: 'employee_offboarding',
        recalculated: false,
        syncPending: !lifecycleSync
      });

      const localizedLastDay = new Date(`${lastWorkingDay}T00:00:00`).toLocaleDateString(
        language === 'ar' ? 'ar' : 'en-US',
        { month: 'long', day: 'numeric', year: 'numeric' }
      );
      const notificationTitle = t('drawerModals.offboard.notificationTitle', 'Offboarding initiated');
      const notificationMessage = t('drawerModals.offboard.notificationMessage', {
        date: localizedLastDay,
        defaultValue: 'Your offboarding process has been initiated. Your last working day is {{date}}.'
      });

      // Send Notification to Employee
      const notifRef = push(ref(database, `${basePath}/notifications`));
      await set(notifRef, {
        title: notificationTitle,
        message: notificationMessage,
        type: 'info',
        timestamp: Date.now(),
        read: false
      });

      // Send Email Notification
      try {
        if (!currentUser) throw new Error('Authentication required');
        const token = await currentUser.getIdToken();
        await fetch(`${BACKEND_URL}/send-notification-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            companyId: companyUID,
            employeeId,
            subject: t('drawerModals.offboard.emailSubject', 'Offboarding process initiated'),
            message: notificationMessage,
            type: 'info'
          })
        });
      } catch (e) {
        console.error('Email notification failed:', e);
      }

      alert(t('drawerModals.offboard.success', 'Offboarding initiated successfully'));
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Offboarding failed:', error);
      alert(t('drawerModals.offboard.failure', 'Failed to initiate offboarding'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '500px',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left'
        }}
      >
        <div className="modal-header">
          <h2>{t('drawerModals.offboard.title', 'Initiate Offboarding')}</h2>
          <button className="close" onClick={onClose}>&times;</button>
        </div>
        <div className="form-section">
          {/* Employee Name */}
          <div className="input-group" style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.common.employee', 'Employee')}
            </label>
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              color: 'var(--app-heading, #031128)'
            }}>
              {safeEmployeeName}
            </div>
          </div>

          {/* Offboard Type */}
          <div className="input-group" style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.offboard.type', 'Offboarding Type')}
            </label>
            <select
              value={offboardType}
              onChange={(e) => setOffboardType(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)',
                backgroundColor: '#FFFFFF',
                cursor: 'pointer'
              }}
            >
              <option value="resignation">{t('drawerModals.offboard.types.resignation', 'Resignation')}</option>
              <option value="termination">{t('drawerModals.offboard.types.termination', 'Termination')}</option>
              <option value="layoff">{t('drawerModals.offboard.types.layoff', 'Layoff')}</option>
              <option value="retirement">{t('drawerModals.offboard.types.retirement', 'Retirement')}</option>
              <option value="contract_end">{t('drawerModals.offboard.types.contract_end', 'Contract End')}</option>
              <option value="mutual">{t('drawerModals.offboard.types.mutual', 'Mutual Agreement')}</option>
              <option value="other">{t('drawerModals.offboard.types.other', 'Other')}</option>
            </select>
          </div>

          {/* Last Working Day */}
          <div className="input-group" style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.offboard.lastDay', 'Last Working Day')} <span style={{ color: 'var(--app-tone-eb4040, #EB4040)' }}>*</span>
            </label>
            <input
              type="date"
              value={lastWorkingDay}
              onChange={(e) => setLastWorkingDay(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)'
              }}
            />
          </div>

          {/* Reason */}
          <div className="input-group" style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.offboard.reason', 'Reason (Optional)')}
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('drawerModals.offboard.reasonPlaceholder', 'Reason for offboarding...')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)'
              }}
            />
          </div>

          {/* Notes */}
          <div className="input-group" style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.offboard.notes', 'Internal Notes (Optional)')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={uiText("Additional notes...")}
              rows={3}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Warning */}
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#FFF3E0',
            border: '1px solid #FFB74D',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ color: 'var(--app-tone-f57c00, #F57C00)' }}></i>
              <span style={{ fontFamily: 'Montserrat', fontSize: '14px', color: 'var(--app-tone-e65100, #E65100)' }}>
                {t('drawerModals.offboard.warning', 'This will change the employee\'s status to "Offboarding" and notify them.')}
              </span>
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{
          padding: '20px 32px',
          borderTop: '1px solid #E0E0E0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '12px 24px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: "var(--app-neutral-ink, #031128)",
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
            data-light-control={"quiet"}>
            {t('common:buttons.cancel', 'Cancel')}
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !lastWorkingDay}
            style={{ '--light-control-fill': saving || !lastWorkingDay ? '#CCCCCC' : '#EB4040',
              padding: '12px 24px',
              backgroundColor: saving || !lastWorkingDay ? '#CCCCCC' : '#EB4040',
              border: 'none',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: '#FFFFFF',
              cursor: saving || !lastWorkingDay ? 'not-allowed' : 'pointer'
            }}
            data-light-control={saving || !lastWorkingDay ? "quiet" : "filled"}>
            {saving ? t('drawerModals.offboard.saving', 'Processing...') : t('drawerModals.offboard.submit', 'Initiate Offboarding')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OffboardModal;
